/**
 * Activities API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Activity,
  ActivityCreateData,
  ActivityUpdateData,
  ActivityFilters,
} from '@/types'

export const activitiesApi = {
  /**
   * List activities with filtering and pagination
   */
  async list(params?: PaginationParams & ActivityFilters): Promise<PaginatedResponse<Activity>> {
    const response = await apiClient.get<PaginatedResponse<Activity>>('/activities', { params })
    return response.data
  },

  /**
   * Get activity by ID
   */
  async get(id: string): Promise<Activity> {
    const response = await apiClient.get<ApiResponse<Activity>>(`/activities/${id}`)
    return response.data.data
  },

  /**
   * Create a new activity
   */
  async create(data: ActivityCreateData): Promise<Activity> {
    const response = await apiClient.post<ApiResponse<Activity>>('/activities', data)
    return response.data.data
  },

  /**
   * Update an activity
   */
  async update(id: string, data: ActivityUpdateData): Promise<Activity> {
    const response = await apiClient.put<ApiResponse<Activity>>(`/activities/${id}`, data)
    return response.data.data
  },

  /**
   * Delete an activity
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/activities/${id}`)
  },

  /**
   * Get activity feed for current user
   */
  async getFeed(params?: PaginationParams): Promise<PaginatedResponse<Activity>> {
    const response = await apiClient.get<PaginatedResponse<Activity>>('/activities/feed', { params })
    return response.data
  },

  /**
   * Get upcoming follow-ups
   */
  async getFollowUps(params?: PaginationParams): Promise<PaginatedResponse<Activity>> {
    const response = await apiClient.get<PaginatedResponse<Activity>>('/activities/follow-ups', { params })
    return response.data
  },

  /**
   * Get activity types
   */
  async getTypes(): Promise<{ value: string; label: string; icon: string }[]> {
    const response = await apiClient.get<ApiResponse<{ value: string; label: string; icon: string }[]>>(
      '/activities/types'
    )
    return response.data.data
  },
}
